<template>
  <div>
    <b-modal
      v-if="isCheckableForCandidate"
      id="checkDocumentForCandidate"
      ref="checkDocumentForCandidateModal"
      :title="uLang('app.check_for_candidate')"
      size="lg"
      :hideFooter="true"
    >
      <check
        :relationId="relation.id"
        :trajectoryEnrolmentId="trajectoryEnrolment.id"
        :milestoneProgressId="milestoneProgress.id"
        :actionProgressId="actionProgress.id"
        :checkId="getCandidateCheck.id"
        :showPreview="false"
        @checked="onCheckedForCandidate"
      />
    </b-modal>
    <div v-if="!componentLoading">
      <div
        class="alert alert-danger mt-2 mb-4"
        role="alert"
        v-if="!relationHasEmail"
      >
        <strong>
          {{
            uLang("app.missing_email_for_relation", {
              relation: relation.full_name,
            })
          }}
          <router-link
            target="_blank"
            :to="{ name: 'admin.relations.edit', params: { id: relation.id } }"
          >
            {{ uLang("app.click_here_to_edit") }}
          </router-link>
        </strong>
      </div>

      <button type="button" @click="showDocument = true" class="btn btn-brand">
        {{ uLang("app.show_pdf_in_new_tab") }}
      </button>

      <portal to="modals" v-if="showDocument">
        <b-modal
          v-model="showDocument"
          :hideFooter="true"
          :title="uLang('app.document')"
          size="lg"
        >
          <file-preview
            :url="
              route(
                'relations.trajectory-enrolments.milestone-progresses.action-progresses.action-document.show',
                {
                  relation: relation.id,
                  trajectory_enrolment: trajectoryEnrolment.id,
                  milestone_progress: milestoneProgress.id,
                  action_progress: actionProgress.id,
                  downloadType: 'stream',
                }
              )
            "
          />
        </b-modal>
      </portal>

      <b-btn
        v-if="checks.length > 0 && canSeeChecks"
        v-b-toggle.notes
        variant="success"
        class="pull-right"
      >
        <i class="fa fa-lg fa-eye"></i> {{ uLang("app.toggle_notes") }}
      </b-btn>

      <button
        v-if="isCheckableForCandidate"
        type="button"
        class="btn btn-brand pull-right mr-1"
        @click="openCheckDocumentForCandidateModal"
      >
        {{ uLang("app.check_for_candidate") }}
      </button>

      <b-collapse id="notes" class="mt-4">
        <b-card>
          <h2>{{ uLang("app.notes") }}</h2>
          <b-alert
            variant="secondary"
            v-for="check in checks"
            v-if="check.checker"
            :key="check.id"
            show
            dismissible
          >
            <div>
              {{ check.updated_at | date }}, {{ check.checker.full_name }}
            </div>
            <div
              :class="{ 'use-newlines': !isPreformatted(check.notes) }"
              v-html="check.notes"
            ></div>
          </b-alert>
        </b-card>
      </b-collapse>

      <div class="action-document-form my-4">
        <placeholder
          v-for="(template, index) in template_items"
          :key="index"
          :text="template"
          v-model="form[getFieldName(template)]"
          :action-document-field-names="action_document_field_names"
          :errors="errors"
          :disabled="calculateDisableInput(actionProgress, index, template)"
          @form-dirty-inner="onFormDirty"
          :trajectory-enrolment="trajectoryEnrolment"
          :action-progress="actionProgress"
          :check="false"
          @send-event-to-form="sendEventToForm"
        />
      </div>

      <action-document-version-form-button
        :data="data"
        :action-progress="actionProgress"
        :working="working"
        :can-mail="relationHasEmail"
        :errors="errors"
        @submit="processForm"
        :filling-error="fillingError"
      />

      <!-- If disabling save draft button, delete this margin-right -->
      <!-- <div style="float: right; font-size: 0.7em; margin-top: -1em; margin-right: 140px;"> -->
      <div style="float: right; font-size: 0.7em; margin-top: -1em;">
        <span v-if="switchSaveDraft">
          {{ uLang("app.saving_draft") }}...
        </span>
        <span v-else-if="saveDraftTime != ''" :key="saveDraftTime">
          {{ uLang("app.last_saved_draft") }}: 
          {{ formattedSaveDraftTime }}
          </span>
      </div>

      <!-- Allowing manually save draft by now as we disabled the autodrafts -->
      <!-- If disabling save draft button, delete the margin-right for the div above -->
      <!-- <div class="center-button" style="position: absolute; bottom: 10px;">
        <button role="button" class="btn btn-info"
        @click="saveDraft(true, true)">
          <i aria-hidden="true" class="fa fa-floppy-o"></i>
          {{ uLang("app.save_draft") }}
        </button>
      </div> -->

    </div>

    <rea-loader
      v-if="componentLoading"
      :loading="componentLoading"
    ></rea-loader>

    <b-modal size="xl" ref="my-modal" hide-footer title="" :close-on-backdrop="false">
      <b-card :header="uLang('app.edit')" header-tag="h4" class="bg-default-card">
        <div id="my-modal-content" class="d-block">
          <div class="form-group row">
            <label for="subject" class="col-form-label col-md-3 col-xl-2 col-sm-12">
              {{ uLang('app.subject_or_short_description') }}:*
            </label> 
            <div class="col-md-9 col-xl-10 col-sm-12">
              <textarea rows="3" id="subject" v-model="modalSubject" name="subject" required="required" class="form-control"></textarea>
            </div>
          </div>
          <div class="form-group row">
            <label for="body" class="col-form-label col-md-3 col-xl-2 col-sm-12">
              {{ uLang('app.body_or_explanation') }}:*
            </label>
            <div class="col-md-9 col-xl-10 col-sm-12">
              <textarea rows="3" id="body" name="body" v-model="modalBody" required="required" class="form-control"></textarea>
            </div>
          </div>
          <div class="form-group row">
            <label for="reporting_log_include" class="col-form-label col-md-3 col-xl-2 col-sm-12">
              {{ uLang('app.reporting_log_include') }}:
            </label>
            <div class="col-md-9 col-xl-10 col-sm-12">
              <select style="width: 100%; padding: 6px; border: 1px solid #ccc;" name="reporting_log_include" id="reporting_log_include" v-model="modalReportLogInclude">
                <option class="multiselect__element" value="yes">{{ uLang('app.yes') }}</option>
                <option class="multiselect__element" value="no">{{ uLang('app.no') }}</option>
              </select>
            </div>
          </div>
          
          <div class="form-group row">
            <label for="duration" class="col-form-label col-md-3 col-xl-2 col-sm-12">
              {{ uLang('app.duration') }}:
            </label>
            <div class="col-md-9 col-xl-10 col-sm-12">
              <TimeInput
                v-model="modalDuration"
                name="duration"
                :required="false"
                labelPosition="right"
              />
            </div>
          </div>  

          <div class="form-group row">
            <label for="body" class="col-form-label col-md-3 col-xl-2 col-sm-12">
              {{ uLang('app.action_date') }}:*
            </label>
            <div class="col-md-9 col-xl-10 col-sm-12">
              <vue-pikaday
                class="form-control"
                :options="{
                    format: 'DD-MM-YYYY',
                    i18n: {
                        previousMonth : 'Vorige maand',
                        nextMonth     : 'Volgende maand',
                        months        : ['Januari','Februari','Maart','April','Mei','Juni','Juli','Augustus','September','Oktober','November','December'],
                        weekdays      : ['Zondag','Maandag','Dinsdag','Woensdag','Donderdag','Vrijdag','Zaterdag'],
                        weekdaysShort : ['Zo','Ma','Di','Wo','Do','Vr','Za']
                    },
                    yearRange: [1900,2030],
                    theme: 'rea-theme'
                }"
                v-model="modalActionDate"
              />
            </div>
          </div>
        </div>
        <b-button variant="btn btn-brand" @click="hideModal">{{ uLang('app.save') }}</b-button>
      </b-card>
    </b-modal>
  </div>
</template>

<script>
import Placeholder from "./elements/placeholder";
import Form from "~/Form/Form";
import FormMixin from "~/mixins/form";
import { ReaLoader } from "~/components/helpers";
import { WarningAlert, ConfirmationAlert, SuccessAlert } from "~/Swals";
import { ActionDocumentVersionFormButton } from "~/components/buttons";
import Check from "~/pages/admin/relation/trajectory-enrolments/action-document-versions/check.vue";
import { mapGetters } from 'vuex';
import { filter } from "lodash";
import TimeInput from "~/components/elements/selects/TimeInput.vue";

export default {
  name: "create",

  mixins: [FormMixin],

  components: {
    Placeholder,
    ReaLoader,
    ActionDocumentVersionFormButton,
    check: Check,
    TimeInput,
  },

  props: {
    relation: {
      type: Object,
      required: true,
    },
    trajectoryEnrolment: {
      type: Object,
      required: true,
    },
    milestoneProgress: {
      type: Object,
      required: true,
    },
    actionProgress: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      componentLoading: true,
      working: false,
      swal: false,
      form: new Form({
        submit_type: "submit",
        deny_terms_reason: "",
      }),
      successText: "submission_sent_two",
      data: null,
      template_items: [],
      action_document_field_names: [],
      checks: [],
      errors: {},
      regexChecker: /^\[form\.([a-zA-Z_]+)\.([a-zA-Z0-9]+)\]$/i,
      showDocument: false,
      isFormDirty: false,
      fillingError: "",

      modalLogId: 0,
      modalSubject: "",
      modalBody: "",
      modalDuration: '',
      enrolmentId: '',
      modalActionProgressId: 0,
      modalReportLogInclude: "yes",
      modalActionDate: "",
      draftInterval: null,
      switchSaveDraft: false,
      saveDraftTime: '',
      tabId: "",
      deviceId: "",
      autoKicked: false,
      isLockedWarning: false,
    };
  },

  mounted() {

    // This code was here, but Pao set it to null on beforeDestroy, commenting it to not mess with it - maybe should delete
    // window.onbeforeunload = (e) => {
    //   if (this.isFormDirty) {
    //     const message = "Changes you made may not be saved.";
    //     e.returnValue = message;
    //     //this.coachRelease();
    //     return message;
    //   }
    //   //this.coachRelease();
    // };

    this.draftInterval = setInterval(() => {
      this.saveDraft();
    }, 30000);

    // Checks if the report is locked by other user (probably took over)
    this.lockCheckInterval = setInterval(() => {
      this.checkLockValidity();
    }, 3000);

  },

  beforeDestroy() {
    window.onbeforeunload = null; // Why Pao did this???

    window.removeEventListener('unload', this.handleWindowUnload);
    this.handleWindowUnload();

    // if (this.isFormDirty && !this.autoKicked) {
    //   // Force saveDraft on leaving the page  
    //   const filteredForm = this.filterForm();

    //   const payload = JSON.stringify({
    //     data: filteredForm,
    //   });

    //   // Use navigator.sendBeacon to send (POST) the form because the page is unloading so we cannot use axios
    //   navigator.sendBeacon(
    //     route('relations.action-progresses.save-draft', {
    //       action_progress: this.actionProgress.id,
    //     }),
    //     payload
    //   );

    //   console.log("Saving draft before closing...");
        
    // }

    // // Only release lock if is the user leaving the page - not if it's autokick because other user took over this report
    // if(!this.autoKicked) {
    //   this.coachRelease();
    // }

    if (this.draftInterval) {
      clearInterval(this.draftInterval);
    }

    if (this.lockCheckInterval) {
      clearInterval(this.lockCheckInterval);
    }
  },

  beforeRouteLeave(to, from, next) {
    
    if (this.isFormDirty) {
      if (!window.confirm("Leave without saving? Changes will be saved as a draft.")) {
        return;
      }
    }
    next();
  },

  watch: {
    action_document_field_names: function (newVal) {
      this.bamboozleInputsAndHints(newVal);
    },
    isFormDirty() {
      if (this.isFormDirty) { 
         this.$emit('form-dirty', { isDirty: this.isFormDirty });
      }
    },
    
  },

  computed: {
    ...mapGetters([
      'authUser',
    ]),
    relationHasEmail() {
      return this.relation.email !== null && this.relation.email !== "";
    },
    isCheckableForCandidate() {
      return typeof this.getCandidateCheck !== "undefined";
    },
    getCandidateCheck() {
      if (!this.checks || this.checks.length === 0) {
        return undefined;
      }

      return this.checks
        .filter(({ type, agreed }) => type === "candidate" && agreed === null)
        .pop();
    },
    canSeeChecks() {
      return !($hasType("client") || $hasType("candidate"));
    },

    formattedSaveDraftTime() {
      if (!this.saveDraftTime) return '';

      const m = moment(this.saveDraftTime, moment.ISO_8601, true);

      if (m.isValid()) {
        return m.format('DD/MM/YYYY HH:mm:ss');
      }

      // Otherwise it's a message
      return this.saveDraftTime;
    },
  },

  async created() {

    this.tabId = this.generateTabId();
    this.deviceId = this.generateDeviceId();

    window.addEventListener('unload', this.handleWindowUnload);

    const response = await axios.get(
      route('lock.coach.check', { action_progress: this.actionProgress.id }),
      {
        params: {
          deviceId: this.deviceId,
        }
      }
    );

    if (response.data.locked) {
      this.isLockedWarning = true; // To avoid reload on checkLockValidity()
      
      await new ConfirmationAlert({
        text: this.uLang(response.data.message),
        title: this.uLang("app.error"),
        entity: "report", // anything, just to satisfy the default message if needed
        cancelButtonText: this.uLang("app.close"),
        continueButtonText: this.uLang("app.release_report"),
      }).show()
        .then(async () => {
          // User clicked "Take over"
          this.isLockedWarning = false;
          await this.lockReport(); // this will change the lock => "auto-release" + "new lock"
        })
        .catch(() => {
          // User clicked "Close"
          this.$router.push({ name: "dashboard" });
        });

    } else {
      await this.lockReport();
    }

    await this.retrieveTemplate(); // Added await to be sure data is loaded before things as fetchChecksData
    this.$on("afterStore", this.afterSubmit);
    this.$on("afterUpdate", this.afterSubmit);
    this.$on("beforeStore", this.beforeStore);
    this.$on("onError", this.onError);
  },
  
  methods: {
    sendEventToForm() {
      // Must use nextTick for updated DOM first!
      this.$nextTick(() => {
        this.checkFormFilled();
      });
    },
    generateTabId() {
      if (!sessionStorage.getItem("tabId")) {
          sessionStorage.setItem("tabId", Math.random().toString(36).substr(2, 9));
      }
      return sessionStorage.getItem("tabId");
    },

    generateDeviceId() {
      if (!localStorage.getItem("deviceId")) {
        localStorage.setItem("deviceId", Math.random().toString(36).substr(2, 10));
      }
      return localStorage.getItem("deviceId");
    },

    async lockReport() {
      await axios.post(route('lock.coach.tolock', 
        { 
          action_progress: this.actionProgress.id
        }), 
        {
          relationId: this.authUser.id,
          tabId: this.tabId,
          deviceId: this.deviceId,
        }
      );
    },

    filterForm() {
      // Filter the form to only include keys that match the regex pattern
      return Object.keys(this.form)
        .filter(key => /^[A-Za-z0-9]{10}$/.test(key)) // Match keys with the regex pattern
        .reduce((acc, key) => {
          const value = this.form[key];
          if (value !== null && value !== 'null') { // Exclude null values
            acc[key] = value;
          }
          return acc;
        }, {});
    },

    async saveDraft(doAlertToast = false, force = false) {
      // If there's another saveDraft in progress, do not proceed - unless forced (like on leaving the page)
      if(this.switchSaveDraft && !force) {
        console.log('Another saveDraft is already in progress, skipping this one.');
        return
      }

      this.switchSaveDraft = true; // Prevent multiple calls to saveDraft

      const filteredForm = this.filterForm();
      const payload = { ...filteredForm, submit_type: 'autosave', force };

      const { data } = await window.axios.post(
        this.fullBaseUri(),
        payload
      );

      this.isFormDirty = false;
      this.switchSaveDraft = false;
      this.saveDraftTime = data.no_drafts ? data.message : new Date().toISOString();

      if (doAlertToast === true) {
        return new SuccessAlert({
          text: uLang("app.save_draft_completed"),
        }).show();
      }
    },

    calculateDisableInput(actionProgress, index, template) {
      let template_name = this.getFieldName(template);
      if (index === 1) {
        let regex = new RegExp('periode' + ".*" + template_name, "i");
        if (regex.test(this.template_items[0])) {
          return true;
        } else {
          return ('skipped' === actionProgress.action_progress_status.name);
        }
      } else {
        return ('skipped' === actionProgress.action_progress_status.name);
      }
    },
    showMyModal() {
      this.$refs['my-modal'].show();
    },
    hideModal() {
      let formattedDate = ('0' + this.modalActionDate.getDate()).slice(-2) + '-' +
                    ('0' + (this.modalActionDate.getMonth() + 1)).slice(-2) + '-' +
                    this.modalActionDate.getFullYear();

      window.axios.post("/api/admin/logs/save/", {
        action_date: formattedDate,
        log_id: this.modalLogId,
        subject: this.modalSubject,
        body: this.modalBody,
        duration: this.modalDuration,
        enrolmentId: this.enrolmentId,
        action_progress_id: this.modalActionProgressId,
        reporting_log: this.modalReportLogInclude === "yes" ? 1 : 0,
        form_coach_page: 1
      })
      .then(response => {
        document.querySelector('#in-report-span-subject-log-id-' + this.modalLogId).textContent = response.data.data.subject;
        document.querySelector('#in-report-content-p-log-id-' + this.modalLogId).textContent = response.data.data.body.replace(/\n/g, '<br>');
        let [year, month, day] = response.data.data.action_date.split('-');
        document.querySelector('#in-report-span-action-date-log-id-' + this.modalLogId).textContent = `${day}-${month}-${year}`;
        if (response.data.reporting_log === 0) {
          document.querySelector('#in-report-wrapper-log-id-' + this.modalLogId).innerHTML = "";
        }
        this.$refs['my-modal'].hide()
      })
      .catch(error => {
          console.error(error); // handle error
      });
    },

    /**
     * Takes the names of the fields that are available in the template
     * Then puts all the inputs (placeholder components) into the hint wrappers.
     * This way the inputs will actually be placed on the right spot, above the hints.
     *
     * @param names
     */
    bamboozleInputsAndHints(names) {
      this.$nextTick(() => {
        // Here we will bamboozle every input into the span meant for it.
        let len = names.length;
        for (let i = 0; i < len; i++) {
          let name = names[i].name;

          let div = document.getElementsByClassName(name)[0];
          if (typeof div !== "undefined" && div !== null) {
            let input = document.getElementsByName(name)[0];
            div.appendChild(input);
          }
        }

        // Now bamboozle all the hints into the right holders.
        let hintWrappers = document.getElementsByClassName("hint-wrapper");
        let hintLength = hintWrappers.length;
        for (let i = 0; i < hintLength; i++) {
          let index = hintWrappers[i].dataset.wrapperId;
          let hint = document.getElementsByClassName("hint-" + index)[0];
          hintWrappers[i].appendChild(hint);
        }
      });
    },
    checkFormFilled() {
      const filteredForm = Object.keys(this.form)
        .filter(key => /^[A-Za-z0-9]{10}$/.test(key)) // Match keys with the regex pattern
        .reduce((acc, key) => {
          acc[key] = this.form[key];
          return acc;
        }, {});

      const allValuesValid = Object.values(filteredForm).every(value => value !== null && value !== '' && value !== '<p>&nbsp;</p>');
      if (allValuesValid) {
        this.fillingError = "";
      } else {
        this.fillingError = "Vul eerst alle velden in voordat je ze laat controleren";
      }
    },
    onFormDirty(isDirty) {
      this.checkFormFilled();
      this.isFormDirty = true;
    },
    
    getFieldName(formPlaceholder) {
      let fieldName = this.regexChecker.exec(formPlaceholder);

      if (fieldName !== null) {
        return fieldName[2];
      }

      return null;
    },

    async retrieveTemplate() {
      window.axios
        .get(
          route(
            "relations.trajectory-enrolments.milestone-progresses.action-progresses.action-document.index",
            {
              relation: this.relation.id,
              trajectory_enrolment: this.trajectoryEnrolment.id,
              milestone_progress: this.milestoneProgress.id,
              action_progress: this.actionProgress.id,
            }
          )
        )
        .then(({ data }) => {
          // Set up original data in Form
          for (let index in data.action_document_version
            .action_document_field_names) {
            let field =
              data.action_document_version.action_document_field_names[index];

            this.form.originalData[field.name] = null;
            this.form[field.name] = null;
            if (field.action_document_filled_data.length > 0) {
              let value = this.getValue(field);
              this.form.originalData[field.name] = value;
              this.form[field.name] = value;
            }
          }
          this.checkFormFilled();

          this.data = data;
          this.template = this.splitUpTemplate(
            data.action_document_version.template
          );

          this.action_document_field_names =
            data.action_document_version.action_document_field_names;
          this.checks = data.checks;
          this.componentLoading = false;

          this.$nextTick(() => {
            const buttons = document.querySelectorAll('.openModalButton');
            buttons.forEach(button => {
              button.addEventListener('click', (event) => {
                const log_id = event.currentTarget.getAttribute('data-log-id');
                const actionProgressId = event.currentTarget.getAttribute('data-action-progress-id');

                window.axios.get(`/api/admin/logs/${log_id}`, {
                    params: {
                      action_progress_id: actionProgressId
                    }
                  }).then(({ data }) => {
                    this.modalLogId = data.id;
                    this.modalSubject = data.subject;
                    this.modalBody = data.body;
                    console.log('DATA', data);
                    this.modalActionProgressId = actionProgressId;
                    this.enrolmentId = data.trajectoryEnrolment.id;

                    // Get the duration from the report item
                    // DELETE ID-325
                    // const reportItem = data.appearsInReports.find(item => item.pivot && item.pivot.action_progress_id == this.actionProgressId);
                    // this.modalDuration = reportItem && reportItem.pivot ? reportItem.pivot.duration : '';

                    console.log('TimeLogs', data.time_logs);
                    if (Array.isArray(data.time_logs)) {
                      const timeLog = data.time_logs.find(tl => tl.log_id == log_id);
                      this.modalDuration = timeLog ? timeLog.time : '';
                      if(this.enrolmentId === null) {
                        this.enrolmentId = timeLog ? timeLog.trajectory_enrolment_id : '';
                      }
                      // this.enrolmentId = timeLog ? timeLog.trajectory_enrolment_id : null;
                    }
                    
                    this.modalReportLogInclude = (data.appears_in_reports.length > 0 ? "yes" : "no");
                    
                    const [day, month, year] = data.activity_date.split("-");
                    const parsedDate = new Date(year, month - 1, day);

                    this.modalActionDate = parsedDate;

                    this.showMyModal();
                  });
              });
            });
          });
        })
        .catch((error) => {
          this.$snotify.error(error.response.data.message);
        });
    },

    splitUpTemplate(template) {
      let messedUpTemplateItems = template.split(
        /(\[form\.[a-zA-Z_]+\.[a-zA-Z0-9]+\])/i
      );

      let len = messedUpTemplateItems.length;
      let formInputs = [];
      for (let i = 1; i < len; i++) {
        if (this.regexChecker.test(messedUpTemplateItems[i])) {
          let matches = this.regexChecker.exec(messedUpTemplateItems[i]);

          messedUpTemplateItems[i - 1] =
            messedUpTemplateItems[i - 1] +
            '<span class="target-holder ' +
            matches[2] +
            '" data-name="' +
            matches[2] +
            '"></span>';

          formInputs.push(messedUpTemplateItems.splice(i, 1));
        }
      }

      this.template_items = [
        messedUpTemplateItems.join(""),
        ...formInputs.flat(),
      ];

      console.log("Template items:", this.template_items);
    },

    getValue(field) {
      let data = field.action_document_filled_data;

      if (data.length > 1) {
        return _.last(_.uniqBy(data, "version")).value;
      }
      return data[0].value;
    },

    shouldResetForm() {
      return false;
    },

    /**
     * Handle the submission from our 'submit button'
     */
    async processForm(event) {
      this.working = true;
      this.isFormDirty = false;
      this.$emit('form-dirty', { isDirty: this.isFormDirty });
      // Get the correct submit type, and what message to show on success.
      let { submitType, successText, options = null } = event;

      // Set the deny_terms reason if we have options available

      if (options !== null && options.hasOwnProperty("deny_terms_reason")) {
        this.form.deny_terms_reason = options.deny_terms_reason;
      }

      this.form.submit_type = submitType;

      await this.store();

      this.working = false;
    },

    /**
     * When we get an error on submission, handle the error
     *
     * @param error
     */
    onError(error) {
      this.working = false;

      if (error.errors === undefined || error.errors === null) {
        new WarningAlert({
          title: uLang("app.validation_error"),
          text: error.message,
        }).show();
        return;
      }

      this.errors = error.errors;
      new WarningAlert({
        title: uLang("app.validation_error"),
        text: uLang("app.fill_all_fields"),
      }).show();
    },

    /**
     * After successful submission, show the success text
     *
     * @param data
     */
    afterSubmit(data) {
      this.coachRelease();

      new SuccessAlert({ text: uLang("app." + this.successText) }).show();

      this.$router.push({
        name: "dashboard",
      });
    },

    openCheckDocumentForCandidateModal() {
      this.$refs.checkDocumentForCandidateModal.show();
    },

    onCheckedForCandidate() {
      this.retrieveTemplate();
    },

    isPreformatted(note) {
      return note && note.includes("<br");
    },
    isDirty() {
      return (
        this.form &&
        JSON.stringify(this.form.originalData) !==
          JSON.stringify(this.form.data)
      );
    },

    // Release the lock
    coachRelease() {
      const data = JSON.stringify({ 
        relation_id: this.authUser.id,
        tabId: this.tabId,
        deviceId: this.deviceId,
      });

      navigator.sendBeacon(route('lock.coach.release', {action_progress: this.actionProgress.id}), data);
    },

    /**
     * Actions to do on uloading the page - even if user closes the tab/browser
     */
    handleWindowUnload() {
      if (this.isFormDirty && !this.autoKicked) {
        const payload = { ...this.filterForm(), submit_type: 'autosave' };
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });

        navigator.sendBeacon(this.fullBaseUri(), blob);
      }

      // Only release lock if is the user leaving the page - not if it's autokick because other user took over this report
      if(!this.autoKicked) {
        this.coachRelease();
      }
    },

    /**
     * Checks if this report is already locked by other user (probably took over) to "kick off" this user
     */
    async checkLockValidity() {

      // Only check if we are not already showing the warning
      if(!this.isLockedWarning) {
        const response = await axios.get(route('lock.coach.validate', {
          action_progress: this.actionProgress.id,
        }), {
          params: {
            deviceId: this.deviceId,
          }
        });

        if (!response.data.valid) {
          this.autoKicked = true;
          window.location.reload(); // Reload to trigger the takeover warning
        }
      }
    },
  },
};
</script>

<style lang="scss">

.center-button {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
}

.alert-secondary {
  color: #383d41;
  background-color: #e2e3e5;
  border-color: #d6d8db;
}

.action-completed {
  background-color: #f3f1f6;
  padding: 8px 15px;
  border: 1px solid #eceeef;

  p {
    margin: 0;
    line-height: 20px;
    font-weight: 500;
    letter-spacing: 0px;
  }
}

.use-newlines {
  overflow-wrap: break-word;
}
</style>
